import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Flex, Row, Skeleton, Typography } from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EChart, type EChartClick, type EChartOption } from '../../components/EChart';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { fetchPartsSummary, type SalesLinesFilter } from './api';
import { unitsTooltipFormatter } from './chart-tooltip';
import { DeadStockDrawer } from './DeadStockDrawer';
import { formatUnits, plotValue } from './figures';
import { SalesLinesDrawer } from './SalesLinesDrawer';
import { Tile } from './Tile';

const { Text } = Typography;

interface SalesDrill {
  filter: SalesLinesFilter;
  title: string;
}

/** Insights, Parts: stock as AutoCount holds it, dead stock, and the year's top sellers. */
export function PartsTab({ year }: { year: number }): ReactNode {
  const { t } = useTranslation();
  const [salesDrill, setSalesDrill] = useState<SalesDrill | null>(null);
  const [deadOpen, setDeadOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['analytics', 'parts', 'summary', year],
    queryFn: () => fetchPartsSummary(year),
  });

  const option = useMemo((): EChartOption => {
    if (summary.data === undefined) return {};
    const { topSellers } = summary.data;
    const axisText = { color: 'var(--ink-muted)' };
    // A horizontal bar chart draws its first category at the bottom, so the
    // best seller goes last to sit on top.
    const ordered = [...topSellers].reverse();
    return {
      grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: unitsTooltipFormatter(
          (_id, index) => {
            const s = ordered[index];
            return s === undefined ? undefined : formatMoney(s.revenue);
          },
          (index) => {
            const s = ordered[index];
            return s === undefined ? '' : `${s.itemCode} · ${formatUnits(s.qty)}`;
          },
        ),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: { type: 'value', axisLabel: axisText, splitLine: { lineStyle: { color: 'var(--line)' } } },
      yAxis: {
        type: 'category',
        data: ordered.map((s) => s.itemCode),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      series: [
        {
          id: 'REVENUE',
          name: t('insights.parts.revenue'),
          type: 'bar',
          barMaxWidth: 20,
          cursor: 'pointer',
          itemStyle: { color: 'var(--series-1)' },
          emphasis: { focus: 'series' },
          data: ordered.map((s) => plotValue(s.revenue)),
        },
      ],
    };
  }, [summary.data, t]);

  if (summary.error !== null) {
    const denied = summary.error instanceof ApiError && summary.error.status === 403;
    return <Alert type="error" showIcon message={t(denied ? 'insights.noAccess' : 'insights.loadFailed')} />;
  }
  if (summary.data === undefined) return <Skeleton active paragraph={{ rows: 8 }} />;

  const data = summary.data;
  if (data.stockDataAsOf === null && data.salesDataAsOf === null) {
    return <Alert type="info" showIcon message={t('insights.parts.noStock')} />;
  }

  const openSeller = (click: EChartClick): void => {
    const ordered = [...data.topSellers].reverse();
    const seller = ordered[click.dataIndex];
    if (seller === undefined) return;
    setSalesDrill({
      filter: { year, itemCode: seller.itemCode, stream: 'PARTS' },
      title: t('insights.parts.sellerTitle', { item: seller.itemCode, year: String(year) }),
    });
  };

  const hasStock = data.stockDataAsOf !== null;

  return (
    <Flex vertical gap={16}>
      <Flex vertical>
        <Text type="secondary">
          {hasStock
            ? t('insights.asRead', { at: formatDateTime(data.stockDataAsOf ?? '') })
            : t('insights.parts.noStock')}
        </Text>
      </Flex>

      {hasStock && data.negativeParts > 0 && (
        <Alert type="warning" showIcon message={t('insights.parts.negative', { count: data.negativeParts })} />
      )}

      {hasStock && (
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} xl={8}>
            <Tile id="stock-value" label={t('insights.parts.stockValue')} value={formatMoney(data.stockValue)}>
              <Text type="secondary">{t('insights.parts.stockValueHelp')}</Text>
            </Tile>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Tile id="in-stock" label={t('insights.parts.inStock')} value={String(data.partsInStock)}>
              <Text type="secondary">{t('insights.parts.inStockHelp')}</Text>
            </Tile>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Tile id="dead" label={t('insights.parts.dead')} value={formatMoney(data.deadStock.value)}>
              <Text type="secondary">{t('insights.parts.deadHelp', { parts: data.deadStock.parts })}</Text>
              <Button
                type="link"
                style={{ padding: 0, alignSelf: 'flex-start' }}
                onClick={() => {
                  setDeadOpen(true);
                }}
              >
                {t('insights.parts.seeDead')}
              </Button>
            </Tile>
          </Col>
        </Row>
      )}

      <Card size="small" title={t('insights.parts.topTitle')}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.parts.topHelp')}</Text>
          {data.salesDataAsOf !== null && (
            <Text type="secondary">{t('insights.dataAsOf', { at: formatDateTime(data.salesDataAsOf) })}</Text>
          )}
          {data.topSellers.length === 0 ? (
            <Text type="secondary">{t('insights.parts.noSellers')}</Text>
          ) : (
            <EChart option={option} height={Math.max(200, data.topSellers.length * 28 + 24)} onClick={openSeller} />
          )}
        </Flex>
      </Card>

      <SalesLinesDrawer
        filter={salesDrill?.filter ?? null}
        title={salesDrill?.title ?? ''}
        onClose={() => {
          setSalesDrill(null);
        }}
      />
      <DeadStockDrawer
        open={deadOpen}
        onClose={() => {
          setDeadOpen(false);
        }}
      />
    </Flex>
  );
}
